export default class BaseSettingsManager {
  constructor(options = {}) {
    this.options = options;
    this.pageType = options.pageType || 'digital';
    this.supportedTemplates = options.supportedTemplates || null;
    this.settingsBtn = document.getElementById(options.settingsButtonId || 'settings-btn');
    this.settingsPanel = document.getElementById(options.panelId || 'settings-panel');
    this.settingsOverlay = document.getElementById(options.overlayId || 'settings-overlay');
    this.mobileExploreSection = document.getElementById(options.mobileExploreId || 'mobile-explore-section');
    this.isOpen = false;
    this.userManuallyClosed = false;
    this.exploreBannerVisible = true;
    this.currentTemplate = document.body?.getAttribute('data-clock-template') || null;

    if (!this.settingsPanel || !this.settingsBtn || !this.settingsOverlay) {
      return;
    }

    this.init();
  }

  init() {
    this.bindEvents();
    this.syncToggleStates();
    this.initMobileExplore();
    this.initDesktopPanel();
    this.initTemplateOptions();
    if (typeof this.options.onInit === 'function') {
      this.options.onInit(this);
    }
  }

  bindEvents() {
    if (this.settingsBtn) {
      this.settingsBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggle();
      });
    }

    if (this.settingsOverlay) {
      this.settingsOverlay.addEventListener('click', () => {
        this.close();
      });
    }

    this.bindMobileExploreEvents();

    document.addEventListener('click', (event) => {
      if (!this.isOpen) {
        return;
      }
      if (!this.settingsPanel.contains(event.target) && !this.settingsBtn.contains(event.target)) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    this.bindTemplateEvents();
    this.bindToggleEvents();
  }

  bindTemplateEvents() {
    const newOptions = document.querySelectorAll('.template-preview-option');
    newOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const template = option.dataset.template;
        this.selectTemplate(template);
      });
    });

    const legacyOptions = document.querySelectorAll('.template-option');
    legacyOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const template = option.dataset.template;
        this.selectTemplate(template);
      });
    });
  }

  bindToggleEvents() {
    const formatSegment = document.getElementById('format-segment');
    if (formatSegment) {
      formatSegment.addEventListener('click', (event) => {
        const button = event.target;
        if (!button.classList.contains('segment-option')) {
          return;
        }
        formatSegment.querySelectorAll('.segment-option').forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
        if (typeof this.options.onFormatToggle === 'function') {
          this.options.onFormatToggle(button.dataset.format);
        }
      });
    }

    const secondToggle = document.getElementById('second-toggle');
    if (secondToggle) {
      secondToggle.addEventListener('change', () => {
        this.updateToggleLabelStyle(secondToggle);
        if (typeof this.options.onSecondsToggle === 'function') {
          this.options.onSecondsToggle(secondToggle.checked);
        }
      });
    }

    const dateToggle = document.getElementById('date-toggle');
    if (dateToggle) {
      dateToggle.addEventListener('change', () => {
        this.updateToggleLabelStyle(dateToggle);
        if (typeof this.options.onDateToggle === 'function') {
          this.options.onDateToggle(dateToggle.checked);
        }
      });
    }
  }

  initTemplateOptions() {
    if (!this.supportedTemplates) {
      return;
    }
    const templateOptions = document.querySelectorAll('.template-preview-option, .template-option');
    templateOptions.forEach((option) => {
      const template = option.dataset.template;
      if (this.supportedTemplates.includes(template)) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
      }
    });
  }

  initMobileExplore() {
    if (window.innerWidth < 1024 && this.mobileExploreSection) {
      this.mobileExploreSection.classList.remove('hidden');
    }
  }

  initDesktopPanel() {
    if (this.options.autoOpenDesktop === false) {
      return;
    }
    setTimeout(() => {
      if (window.innerWidth >= 1024 && !this.userManuallyClosed) {
        this.isOpen = true;
        this.settingsPanel.classList.add('open');
        document.body.classList.add('settings-panel-open');
        if (typeof this.options.onPanelOpened === 'function') {
          this.options.onPanelOpened(this);
        }
      }
    }, 100);
  }

  bindMobileExploreEvents() {
    const bannerClose = document.getElementById('banner-close');
    if (bannerClose) {
      bannerClose.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.mobileExploreSection) {
          this.mobileExploreSection.classList.add('hidden');
          this.exploreBannerVisible = false;
        }
      });
    }
  }

  updateToggleLabelStyle() {
    // 预留扩展：标题样式暂不调整
  }

  syncToggleStates() {
    if (typeof this.options.getCurrentSettings !== 'function') {
      return;
    }
    const state = this.options.getCurrentSettings();
    if (!state) {
      return;
    }

    const formatSegment = document.getElementById('format-segment');
    if (formatSegment && typeof state.is24Hour === 'boolean') {
      const target = state.is24Hour
        ? formatSegment.querySelector('[data-format="24h"]')
        : formatSegment.querySelector('[data-format="12h"]');
      formatSegment.querySelectorAll('.segment-option').forEach((btn) => btn.classList.remove('active'));
      if (target) {
        target.classList.add('active');
      }
    }

    const secondToggle = document.getElementById('second-toggle');
    if (secondToggle && typeof state.showSeconds === 'boolean') {
      secondToggle.checked = state.showSeconds;
      this.updateToggleLabelStyle(secondToggle);
    }

    const dateToggle = document.getElementById('date-toggle');
    if (dateToggle) {
      if (typeof state.showDate === 'boolean') {
        dateToggle.checked = state.showDate;
      }
      if (typeof state.showDateYear === 'boolean') {
        dateToggle.checked = state.showDateYear;
      }
      this.updateToggleLabelStyle(dateToggle);
    }
  }

  selectTemplate(template) {
    if (!template) {
      return;
    }
    this.currentTemplate = template;

    document.querySelectorAll('.template-preview-option').forEach((option) => {
      option.classList.toggle('active', option.dataset.template === template);
    });
    document.querySelectorAll('.template-option').forEach((option) => {
      option.classList.toggle('active', option.dataset.template === template);
    });

    if (typeof this.options.onTemplateSelect === 'function') {
      this.options.onTemplateSelect(template);
    }

    if (typeof this.options.onAfterTemplateSelect === 'function') {
      this.options.onAfterTemplateSelect(template);
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.userManuallyClosed = false;
    requestAnimationFrame(() => {
      this.settingsOverlay.classList.add('active');
      this.settingsPanel.classList.add('open');
      document.body.classList.add('settings-panel-open');
      this.syncToggleStates();
      if (typeof this.options.onPanelOpened === 'function') {
        this.options.onPanelOpened(this);
      }
    });

    if (window.navigationManager) {
      window.navigationManager.forceShow();
    }

    setTimeout(() => {
      this.settingsPanel?.focus?.();
    }, 100);
  }

  close() {
    this.isOpen = false;
    if (window.innerWidth >= 1024) {
      this.userManuallyClosed = true;
    }

    requestAnimationFrame(() => {
      this.settingsPanel.classList.remove('open');
      this.settingsOverlay.classList.remove('active');
      setTimeout(() => {
        document.body.classList.remove('settings-panel-open');
      }, 50);
    });
  }

  handleResize() {
    if (!this.settingsPanel) {
      return;
    }

    if (window.innerWidth >= 1024) {
      if (!this.userManuallyClosed && !this.settingsPanel.classList.contains('open')) {
        this.settingsPanel.classList.add('open');
        this.isOpen = true;
      }
      if (this.mobileExploreSection) {
        this.mobileExploreSection.classList.add('hidden');
      }
    } else {
      this.userManuallyClosed = false;
      if (this.isOpen) {
        this.settingsPanel.classList.remove('open');
        this.settingsOverlay.classList.remove('active');
        this.isOpen = false;
      }
      if (this.mobileExploreSection && this.exploreBannerVisible) {
        this.mobileExploreSection.classList.remove('hidden');
      }
    }
  }
}
